import * as linkify from 'linkifyjs'

export default ({ Vue }) => {
  return {
    bind (el, binding) {
      updateLinkified(el, binding)
    },
    update (el, binding) {
      updateLinkified(el, binding)
    },
    componentUpdated (el, binding) {
      updateLinkified(el, binding)
    },
    unbind (el) {
      delete el.dataset.linkifyProcessedText
    }
  }
}

function updateLinkified (el, binding) {
  // Handle v-linkify:options syntax where binding.arg is 'options' and binding.value is the options object
  const options = binding.arg === 'options' ? (binding.value || {}) : (binding.value?.options || binding.value || {})
  const target = options.target || '_blank'

  const currentText = el.textContent || el.innerText || ''
  const hasLinkifiedLinks = el.querySelector('a.linkified') !== null
  const lastProcessedText = el.dataset.linkifyProcessedText

  if (lastProcessedText === currentText && hasLinkifiedLinks) {
    return
  }

  el.dataset.linkifyProcessedText = currentText

  // Check if element already has HTML content (from v-html)
  const hasHtmlContent = el.innerHTML !== el.textContent

  if (hasHtmlContent) {
    // Element has HTML structure, process text nodes while preserving HTML
    processHtmlContent(el, target)
  } else {
    // Plain text content, process normally
    processPlainText(el, target)
  }
}

function processPlainText (el, target) {
  // Get the text content
  const originalText = el.textContent || el.innerText || ''

  if (!originalText.trim()) {
    return
  }

  // Find all links in the text
  const links = linkify.find(originalText)

  if (links.length === 0) {
    return
  }

  el.replaceChildren(linkifiedFragment(originalText, links, target))
}

function processHtmlContent (el, target) {
  // Clone the element to work with
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = el.innerHTML

  // Get all text nodes and process them
  const walker = document.createTreeWalker(
    tempDiv,
    NodeFilter.SHOW_TEXT,
    null,
    false
  )

  // Collect all text nodes first (to avoid modifying tree while walking)
  // Skip text nodes that are already inside linkified links
  const textNodes = []
  let node = walker.nextNode()
  while (node) {
    let parent = node.parentNode
    let isInsideLinkified = false
    while (parent && parent !== tempDiv) {
      if (parent.tagName === 'A' && parent.classList.contains('linkified')) {
        isInsideLinkified = true
        break
      }
      parent = parent.parentNode
    }

    if (node.textContent.trim() && !isInsideLinkified) {
      textNodes.push({
        node: node,
        text: node.textContent
      })
    }
    node = walker.nextNode()
  }

  // Process each text node
  textNodes.forEach(({ node: textNode, text }) => {
    const links = linkify.find(text)

    if (links.length === 0) {
      return
    }

    textNode.parentNode.replaceChild(linkifiedFragment(text, links, target), textNode)
  })

  // Update the original element with processed content
  el.innerHTML = tempDiv.innerHTML
}

const LINK_SCHEMES = /^(https?|mailto|tel):/i

// The text with each link in it as an anchor. Built with DOM calls, never an HTML string: linkify keeps quotes
// and angle brackets inside a link, so a string would let a message's link close its href and add markup.
function linkifiedFragment (text, links, target) {
  const fragment = document.createDocumentFragment()
  const sortedLinks = [...links].sort((a, b) => a.start - b.start)
  let lastIndex = 0

  sortedLinks.forEach(link => {
    const href = link.href || link.value

    if (!LINK_SCHEMES.test(href)) {
      return
    }

    fragment.append(text.substring(lastIndex, link.start))

    const anchor = document.createElement('a')
    anchor.setAttribute('href', href)
    anchor.setAttribute('target', target)
    anchor.setAttribute('rel', 'noopener noreferrer')
    anchor.className = 'linkified'
    anchor.textContent = text.substring(link.start, link.end)
    fragment.append(anchor)

    lastIndex = link.end
  })

  fragment.append(text.substring(lastIndex))

  return fragment
}
